import { Injectable, Injector } from '@angular/core';
import { HttpClient, HttpResponse, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { Subject } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { environment } from 'src/environments/environment';
import { map } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class DataService {
  // Define the internal Subject we'll use to push the command count
  public pendingCommandsSubject = new Subject<number>();
  public pendingCommandCount = 0;

  // Provide the *public* Observable that clients can subscribe to
  public pendingCommands$: Observable<number>;

  constructor(
    public http: HttpClient,
    private inj: Injector,
    private _authenticationService: AuthService,
  ) {
    this.pendingCommands$ = this.pendingCommandsSubject.asObservable();
  }

  public get<T>(url: string, params?: any): Observable<T> {
    const apiUrl = environment.apiUrl + '/' + url;
    return this.http
      .get<T>(apiUrl, {
        params: this.buildUrlSearchParams(params),
      })
      .pipe(map((response) => this.transformLogoFields(response as any)));
  }

  public getFull<T>(url: string): Observable<HttpResponse<T>> {
    const apiUrl = environment.apiUrl + '/' + url;
    return this.http.get<T>(apiUrl, { observe: 'response' });
  }

  public post<T>(url: string, data?: any, params?: any): Observable<T> {
    const apiUrl = environment.apiUrl + '/' + url;
    return this.http
      .post<T>(apiUrl, data, { params: params })
      .pipe(map((response) => this.transformLogoFields(response as any)));
  }

  public put<T>(url: string, data?: any, params?: any): Observable<T> {
    const apiUrl = environment.apiUrl + '/' + url;
    return this.http
      .put<T>(apiUrl, data, { params: params })
      .pipe(map((response) => this.transformLogoFields(response as any)));
  }

  public delete<T>(url: string): Observable<T> {
    const apiUrl = environment.apiUrl + '/' + url;
    return this.http
      .delete<T>(apiUrl)
      .pipe(map((response) => this.transformLogoFields(response as any)));
  }

  private buildUrlSearchParams(params: any): HttpParams {
    let searchParams = new HttpParams();
    for (const key in params) {
      if (params.hasOwnProperty(key)) {
        searchParams = searchParams.append(key, params[key]);
      }
    }
    return searchParams;
  }

  public postFile(url: string, data: FormData) {
    const apiUrl = environment.apiUrl + '/' + url;
    return this.http
      .post(apiUrl, data)
      .pipe(map((response) => this.transformLogoFields(response as any)));
  }

  public postText(url: string, data?: any, params?: any): Observable<string> {
    const apiUrl = environment.apiUrl + '/' + url;
    return this.http.post(apiUrl, data, {
      params: params,
      responseType: 'text',
    });
  }

  // Eski surum her seviyede {...payload} spread klonu olusturup tum agaci kopyaliyordu;
  // 10k+ satirli grid yanitlarinda ana thread'i kilitleyen yuzbinlerce gereksiz nesne
  // kopyasi demekti. HTTP yaniti cagirana ait taze JSON oldugu icin YERINDE tek gecis
  // guvenlidir ve alokasyonsuz calisir. Davranis birebir ayni: companyLogoId GUID'lerini
  // indirme URL'sine cevirir (companyLogoUrl/companyLogo alanlari).
  private transformLogoFields(payload: any): any {
    this.applyLogoTransformInPlace(payload, 0);
    return payload;
  }

  private applyLogoTransformInPlace(node: any, depth: number): void {
    if (node == null || typeof node !== 'object' || depth > 32) {
      return;
    }

    if (Array.isArray(node)) {
      for (const item of node) {
        this.applyLogoTransformInPlace(item, depth + 1);
      }
      return;
    }

    for (const key of Object.keys(node)) {
      const value = node[key];
      if (value != null && typeof value === 'object') {
        this.applyLogoTransformInPlace(value, depth + 1);
      }
    }

    const companyLogoId = node.companyLogoId;
    if (this.isGuid(companyLogoId)) {
      const logoUrl = this.buildDownloadFileAttachmentUrl(companyLogoId);
      node.companyLogoUrl = logoUrl;
      node.companyLogo = logoUrl;
    } else if (node.companyLogoUrl || node.companyLogo) {
      const extractedLogoId = this.extractGuidFromString(
        node.companyLogoUrl || node.companyLogo,
      );
      if (extractedLogoId) {
        node.companyLogoId = extractedLogoId;
        const logoUrl = this.buildDownloadFileAttachmentUrl(extractedLogoId);
        node.companyLogoUrl = logoUrl;
        node.companyLogo = logoUrl;
      }
    }
  }

  private buildDownloadFileAttachmentUrl(fileAttachmentId: string): string {
    return `${environment.apiUrl}/api/Emission/DownloadFileAttachment/${fileAttachmentId}`;
  }

  private isGuid(value: unknown): value is string {
    if (typeof value !== 'string') {
      return false;
    }

    const guidRegex =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    return guidRegex.test(value);
  }

  private extractGuidFromString(value: unknown): string | null {
    if (typeof value !== 'string') {
      return null;
    }
    const match = value.match(
      /([0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12})/i,
    );
    if (!match?.[1]) {
      return null;
    }
    return this.isGuid(match[1]) ? match[1] : null;
  }
}
